import type React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import LoaderOverlay from "./components/LoaderOverlay";

interface User {
  id: number;
  name: string;
  login_id: string;
  sis_user_id: string;
  last_seen: string;
  state: string;
}

type DestroyResponse =
  | { message: string; deleted_count: number }
  | {
      message: string;
      requested: number;
      suspended: number;
      failed: Array<{ user_id: number; error: string }>;
    };

type Account = { id: number; name?: string | null; parent_account_id?: number | null };

const apiSeek = axios.create({ baseURL: "/api/seek", withCredentials: true });
const apiRoot = axios.create({ baseURL: "/api", withCredentials: true });

export default function SeekAndDestroy() {
  const [inactiveDays, setInactiveDays] = useState<number>(180);

  // --- selection: require explicit account (no "default")
  const [accountId, setAccountId] = useState<number | null>(null);
  const [accountLabel, setAccountLabel] = useState<string>("Pick an account…");

  // tree data
  const [roots, setRoots] = useState<Account[]>([]);
  const [childrenByParent, setChildrenByParent] = useState<Map<number, Account[]>>(new Map());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loadingParentIds, setLoadingParentIds] = useState<Set<number>>(new Set());
  const [loadingRoots, setLoadingRoots] = useState(false);

  // results
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [selectAll, setSelectAll] = useState(true);

  // ui state
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [deletedCount, setDeletedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // snapshot of the exact users we just suspended (for CSV)
  const [lastSuspendedRows, setLastSuspendedRows] = useState<User[]>([]);

  // dropdown open/close
  const [open, setOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  // cancel support
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // prevent closing/reloading the tab while a long scan runs
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!loading) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [loading]);

  // load roots once
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingRoots(true);
      setError(null);
      try {
        const r = await apiRoot.get("/canvas/accounts/roots");
        if (!alive) return;
        const rootsData: Account[] = (r.data?.accounts as Account[]) || [];
        setRoots(rootsData);

        // Auto-select if there is exactly one root
        if (rootsData.length === 1) {
          const only = rootsData[0];
          setAccountId(only.id);
          setAccountLabel(only.name || `Account ${only.id}`);
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || "Could not load accounts.");
      } finally {
        setLoadingRoots(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const nameOf = (a?: Account | null): string => a?.name || `Account ${a?.id ?? ""}`;

  const ensureChildren = async (parentId: number) => {
    if (childrenByParent.has(parentId)) return; // cached
    setLoadingParentIds((s) => new Set(s).add(parentId));
    try {
      const r = await apiRoot.get("/canvas/accounts/children", { params: { parent_id: parentId } });
      const kids: Account[] = r.data?.accounts || [];
      setChildrenByParent((m) => {
        const next = new Map(m);
        next.set(parentId, kids);
        return next;
      });
    } finally {
      setLoadingParentIds((s) => {
        const next = new Set(s);
        next.delete(parentId);
        return next;
      });
    }
  };

  const toggleExpand = async (id: number) => {
    if (!expanded.has(id)) {
      await ensureChildren(id);
      setExpanded((s) => new Set(s).add(id));
    } else {
      setExpanded((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
    }
  };

  const pickAccount = (acc: Account) => {
    setAccountId(acc.id);
    setAccountLabel(nameOf(acc));
    setOpen(false);
  };

  // Flat list of rows to render (only expanded branches)
  const renderRows = useMemo(() => {
    type Row = { a: Account; depth: number };
    const rows: Row[] = [];
    const walk = (arr: Account[], depth: number) => {
      for (const a of arr) {
        rows.push({ a, depth });
        if (expanded.has(a.id)) {
          const kids = childrenByParent.get(a.id) || [];
          walk(kids, depth + 1);
        }
      }
    };
    const sorted = [...roots].sort((x, y) => (x.name || "").localeCompare(y.name || ""));
    walk(sorted, 0);
    return rows;
  }, [roots, expanded, childrenByParent]);

  const fetchInactive = async () => {
    if (!accountId) {
      setStatus("Please pick an account first.");
      setOpen(true);
      return;
    }

    // cancel any previous in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    const days = Math.max(1, Math.min(365, Number(inactiveDays) || 180));
    setInactiveDays(days);

    setLoading(true);
    setStatus("Fetching inactive users…");
    setError(null);
    setDeletedCount(null);

    try {
      const params = new URLSearchParams({ days: String(days), account_id: String(accountId) });
      const url = `/users?${params.toString()}`;
      const res = await apiSeek.get(url, { signal: abortRef.current.signal });

      if (!Array.isArray(res.data)) {
        setUsers([]);
        setSelectedUserIds(new Set());
        setSelectAll(false);
        setStatus("Unexpected response format.");
        return;
      }

      const usersFetched: User[] = res.data;
      setUsers(usersFetched);

      const allIds = new Set(usersFetched.map((u) => u.id));
      setSelectedUserIds(allIds);
      setSelectAll(true);
      setStatus(`${usersFetched.length} users found.`);
    } catch (err: any) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || (axios as any).isCancel?.(err)) {
        setStatus("Canceled.");
        return;
      }
      const msg =
        err?.response?.data?.detail ??
        (typeof err?.response?.data === "string" ? err.response.data : err?.message) ??
        "Unknown error";
      setStatus(`Error: ${msg}`);
      setUsers([]);
      setSelectedUserIds(new Set());
      setSelectAll(false);
    } finally {
      setLoading(false);
    }
  };

  const confirmDestroy = async () => {
    if (!accountId || selectedUserIds.size === 0) return;

    // cancel any previous in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    setLoading(true);
    setStatus("Suspending selected users…");
    setError(null);

    try {
      const params = new URLSearchParams({ account_id: String(accountId) });
      const url = `/suspend?${params.toString()}`;

      // snapshot BEFORE mutation
      const requestedIds = Array.from(selectedUserIds);
      const userById = new Map(users.map((u) => [u.id, u]));

      const res = await apiSeek.post<DestroyResponse>(
        url,
        { user_ids: requestedIds },
        { signal: abortRef.current.signal }
      );

      const suspendedCount =
        (res.data as any).suspended ?? (res.data as any).deleted_count ?? 0;

      const rawFailed = (res.data as any).failed;
      const failed: Array<{ user_id: number; error: string }> = Array.isArray(rawFailed) ? rawFailed : [];
      const failedIds = new Set(failed.map((f) => f?.user_id).filter((id): id is number => Number.isFinite(id)));

      const successIds = failed.length > 0 ? requestedIds.filter((id) => !failedIds.has(id)) : requestedIds;

      // snapshot rows for CSV (exact suspended)
      const justSuspendedRows = successIds.map((id) => userById.get(id)).filter(Boolean) as User[];
      setLastSuspendedRows(justSuspendedRows);

      setDeletedCount(suspendedCount);
      setStatus((res.data as any).message || "Done.");

      // remove suspended from table; reselect remaining
      const successSet = new Set(successIds);
      const remaining = users.filter((u) => !successSet.has(u.id));
      setUsers(remaining);
      setSelectedUserIds(new Set(remaining.map((u) => u.id)));
      setSelectAll(remaining.length > 0);
    } catch (err: any) {
      if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || (axios as any).isCancel?.(err)) {
        setStatus("Canceled.");
        return;
      }
      const msg = err?.response?.data?.detail ?? err?.message ?? "Unknown error";
      setStatus(`Error: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId: number) => {
    const next = new Set(selectedUserIds);
    next.has(userId) ? next.delete(userId) : next.add(userId);
    setSelectedUserIds(next);
    setSelectAll(next.size === users.length);
  };

  const toggleAll = () => {
    if (selectAll) {
      setSelectedUserIds(new Set());
      setSelectAll(false);
    } else {
      setSelectedUserIds(new Set(users.map((u) => u.id)));
      setSelectAll(true);
    }
  };

  const exportCsv = () => {
    const rows = lastSuspendedRows.length
      ? lastSuspendedRows
      : users.filter((u) => selectedUserIds.has(u.id));
    if (!rows.length) return;

    const csv = [
      ["ID", "SIS User ID", "Login ID", "Name", "Last Seen", "Login State"],
      ...rows.map((u) => [u.id, u.sis_user_id, u.login_id, u.name, u.last_seen, "suspended"]),
    ]
      .map((r) => r.map((field) => `"${field ?? ""}"`).join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `suspended_users_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onControlsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading && !loadingRoots) {
      e.preventDefault();
      fetchInactive();
    }
  };

  return (
    <motion.div
      className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl p-10"
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4 }}
    >
      <h1 className="text-2xl font-bold tracking-tight text-black mb-4">
        Infuse<span className="text-emerald-600">ED</span> — SeatSmart Optimizer
      </h1>


      <p className="text-sm text-gray-600 mb-6">
        Free up your Canvas licenses and avoid paying for users who are no longer active. Instructure charges per{" "}
        <strong>active</strong> user; suspending inactive accounts frees those licenses for new enrollments and prevents
        unnecessary costs. Select an account or sub-account, set an inactivity window, preview inactive users, and
        optionally suspend the selected accounts. You can also export a CSV of the users you suspend for your records.
        Once you navigate away from this page, the CSV will no longer be available for download.
      </p>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-4 mb-6" onKeyDown={onControlsKeyDown}>
        <div className="min-w-[320px]" ref={pickerRef}>
          <label className="text-sm font-medium block mb-1">Account / Sub-account</label>

          {/* Picker Button */}
          <button
            type="button"
            className="w-full border px-3 py-2 rounded-md bg-white text-left flex items-center justify-between"
            onClick={() => setOpen((o) => !o)}
            disabled={loadingRoots}
          >
            <span className="truncate">{accountLabel}</span>
            <span className="ml-3 text-gray-500">▾</span>
          </button>

          {/* Dropdown */}
          {open && (
            <div className="relative">
              <div className="absolute z-50 mt-2 w-full max-h-[360px] overflow-auto rounded-md border bg-white shadow-lg">
                {roots.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {loadingRoots ? "Loading accounts…" : "No accounts found."}
                  </div>
                )}
                {renderRows.map(({ a, depth }) => {
                  const isExpanded = expanded.has(a.id);
                  const isLoading = loadingParentIds.has(a.id);
                  const icon = isLoading ? "…" : isExpanded ? "▾" : "▸";
                  const knownKids = childrenByParent.get(a.id);

                  return (
                    <div key={a.id} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50">
                      <div
                        className="w-5 text-center cursor-pointer select-none"
                        onClick={() => toggleExpand(a.id)}
                        title={isExpanded ? "Collapse" : "Expand"}
                      >
                        {icon}
                      </div>

                      <div
                        className="flex-1 cursor-pointer"
                        style={{ paddingLeft: depth * 12 }}
                        onClick={async () => {
                          if (!knownKids) {
                            await toggleExpand(a.id);
                          }
                          // pick on click regardless
                          pickAccount(a);
                        }}
                        title={nameOf(a)}
                      >
                        {nameOf(a)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div>
          <label htmlFor="inactiveDays" className="text-sm font-medium block mb-1">
            Days inactive
          </label>
          <input
            id="inactiveDays"
            type="number"
            min={1}
            max={365}
            value={inactiveDays}
            onChange={(e) => setInactiveDays(parseInt(e.target.value || "180", 10))}
            className="border px-3 py-2 rounded-md text-sm w-28"
          />
        </div>

        <button
          className="bg-black text-white px-4 py-2 rounded-md hover:bg-white hover:text-black border hover:border-black transition-all disabled:opacity-60"
          onClick={fetchInactive}
          disabled={loading || loadingRoots || !accountId}
          title={!accountId ? "Pick an account to continue" : ""}
        >
          {loading ? "Loading..." : "Preview Inactive Users"}
        </button>
      </div>

      {loading && (
        <div className="flex items-center gap-2 mb-4 text-gray-600">
          <div className="animate-spin h-5 w-5 border-t-2 border-b-2 border-gray-600 rounded-full" />
          <span>Loading data…</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 border border-red-200 bg-red-50 text-red-700 rounded">
          {error}
        </div>
      )}

      {Array.isArray(users) && users.length > 0 && (
        <>
          <div className="overflow-auto max-h-[500px] border rounded mb-6">
            <table className="w-full text-sm table-fixed border-collapse">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="w-12 p-2">
                    <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                  </th>
                  <th className="w-1/4 p-2">Name</th>
                  <th className="w-1/4 p-2">Login ID</th>
                  <th className="w-1/5 p-2">SIS ID</th>
                  <th className="w-1/5 p-2">Last Seen</th>
                  <th className="w-1/6 p-2">Login State</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.has(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                    </td>
                    <td className="p-2">{user.name || "—"}</td>
                    <td className="p-2">{user.login_id || "—"}</td>
                    <td className="p-2">{user.sis_user_id || "—"}</td>
                    <td className="p-2">{user.last_seen || "—"}</td>
                    <td className="p-2">{user.state || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <button
              className="bg-red-700 text-white px-6 py-2 rounded hover:bg-red-800 transition"
              onClick={confirmDestroy}
              disabled={loading}
            >
              {loading ? "Working..." : `Suspend ${selectedUserIds.size} Selected`}
            </button>

            {deletedCount !== null && (
              <button
                onClick={exportCsv}
                className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800 transition"
              >
                Export CSV
              </button>
            )}
          </div>
        </>
      )}

      {status && (
        <div className="mt-6 p-4 bg-gray-100 border rounded">
          <p>{status}</p>
          {deletedCount !== null && <p className="font-bold">Suspended: {deletedCount}</p>}
        </div>
      )}

      {/* Coffee-break overlay */}
      <LoaderOverlay
        show={loading}
        onCancel={() => {
          abortRef.current?.abort();
        }}
        tips={[
          "This can take a few minutes…",
          "Feel free to grab a coffee ☕",
          "Don’t close or refresh this tab",
          "We’re checking every user’s last activity",
        ]}
      />
    </motion.div>
  );
}
